import * as DialogPrimitive from '@radix-ui/react-dialog';
import React, { type ComponentProps } from 'react';
import { twJoin } from 'tailwind-merge';
import { tv, type VariantProps } from 'tailwind-variants';

import { Flex, Heading, ScrollArea, Text } from '../atoms';
import { DialogCloseButton } from './DialogCloseButton';

const dialogContentVariants = tv({
  base: twJoin(
    'flex flex-col gap-6 md:gap-8 md:rounded-md border-none shadow-lg focus-visible:outline-hidden',
    'rounded-t-3xl overflow-hidden',
    'fixed md:left-[50%] md:top-[50%] bottom-0 md:translate-x-[-50%] md:translate-y-[-50%] z-55',
    'w-full md:w-full md:max-w-3xl',
    'data-[state=open]:animate-fade-zoom-in data-[state=closed]:animate-fade-zoom-out',
  ),
  variants: {
    gutter: {
      true: 'md:p-8 p-6',
      top: 'md:pt-8 pt-6',
      false: '',
    },
    fullScreen: {
      true: twJoin(
        'left-0 top-0 translate-x-0 translate-y-0 rounded-none',
        'max-w-full w-dvw max-h-screen h-screen',
        'md:left-0 md:top-0 md:translate-x-0 md:translate-y-0 md:rounded-none',
        'md:max-w-full md:w-dvw md:max-h-screen md:h-screen',
      ),
    },
    maxHeight: {
      true: 'h-fit max-h-[95dvh]',
      false: '',
    },
    inverted: {
      true: 'bg-slate-950 text-background',
      false: 'bg-background text-foreground',
    },
    animation: {
      slideFromRight: twJoin(
        'data-[state=open]:animate-slide-from-right data-[state=closed]:animate-slide-to-right',
      ),
    },
  },
  defaultVariants: {
    gutter: true,
    maxHeight: true,
  },
});

export interface DialogContentProps
  extends
    VariantProps<typeof dialogContentVariants>,
    ComponentProps<typeof DialogPrimitive.Content> {
  className?: string;
  handleClose?: () => void;
  overlayClassName?: string;
}

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

export { Dialog, DialogClose, DialogPortal, DialogTrigger };

export const dialogOverlayVariants = tv({
  base: twJoin(
    'fixed inset-0 z-50 bg-black/30 data-[state=open]:animate-in data-[state=closed]:animate-out',
    'data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in',
  ),
});

interface DialogOverlayProps
  extends
    VariantProps<typeof dialogOverlayVariants>,
    ComponentProps<typeof DialogPrimitive.Overlay> {}

export function DialogOverlay({ className, ...props }: DialogOverlayProps): React.ReactElement {
  return (
    <DialogPrimitive.Overlay
      className={dialogOverlayVariants({ ...props, className })}
      {...props}
    />
  );
}

export function DialogContent({
  className,
  children,
  gutter,
  fullScreen,
  inverted,
  animation,
  maxHeight,
  overlayClassName,
  handleClose,
  ...props
}: DialogContentProps): React.ReactElement {
  return (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        className={dialogContentVariants({
          gutter,
          inverted,
          fullScreen,
          animation,
          maxHeight: !fullScreen && maxHeight,
          className,
        })}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute right-4 top-4" asChild>
          <DialogCloseButton onClick={handleClose} />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

const dialogHeaderVariants = tv({
  base: 'flex flex-col space-y-1.5 text-center',
  variants: {
    withGutter: {
      true: 'px-6 md:px-8',
      false: '',
    },
  },
  defaultVariants: {
    withGutter: false,
  },
});

export interface DialogHeaderProps
  extends VariantProps<typeof dialogHeaderVariants>, React.HTMLAttributes<HTMLDivElement> {}

export function DialogHeader({
  className,
  withGutter,
  ...props
}: DialogHeaderProps): React.ReactElement {
  return <div className={dialogHeaderVariants({ withGutter, className })} {...props} />;
}

const dialogFooterVariants = tv({
  variants: {
    withGutter: {
      true: 'py-4 px-6 md:px-8',
      false: '',
    },
  },
  defaultVariants: {
    withGutter: false,
  },
});
export interface DialogFooterProps
  extends VariantProps<typeof dialogFooterVariants>, ComponentProps<typeof Flex> {}
export function DialogFooter({
  className,
  withGutter,
  children,
  ...props
}: DialogFooterProps): React.ReactElement {
  return (
    <Flex className={dialogFooterVariants({ withGutter, className })} {...props}>
      {children}
    </Flex>
  );
}

export function DialogTitle({ ...props }: ComponentProps<typeof Heading>): React.ReactElement {
  return (
    <DialogPrimitive.Title asChild {...props}>
      <Heading {...props} />
    </DialogPrimitive.Title>
  );
}

export function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof Text>): React.ReactElement {
  return (
    <DialogPrimitive.Description asChild>
      <Text className={className} {...props} muted />
    </DialogPrimitive.Description>
  );
}

const dialogBodyVariants = tv({
  variants: {
    withGutter: {
      true: 'px-6 md:px-8',
      false: '',
    },
    maxHeight: {
      true: 'max-h-[65dvh] min-h-[25dvh] md:max-h-[60dvh] md:min-h-0',
      false: '',
    },
  },
  defaultVariants: {
    maxHeight: true,
    withGutter: false,
  },
});

export interface DialogBodyProps
  extends VariantProps<typeof dialogBodyVariants>, ComponentProps<typeof ScrollArea> {}

export function DialogBody({
  children,
  maxHeight,
  withGutter,
  className,
  ...props
}: DialogBodyProps): React.ReactElement {
  return (
    <ScrollArea className={dialogBodyVariants({ maxHeight, withGutter, className })} {...props}>
      {children}
    </ScrollArea>
  );
}
